import { apiRequest } from './axiosConfig';
import { AIAgent } from '../../types';

export interface CreateAgentRequest {
  title: string;
  description?: string;
  model_type: string;
  prompt_template: string;
  is_active?: boolean;
  category?: string;
  category_id?: number;
  base_color?: string;
}

export interface UpdateAgentRequest {
  title?: string;
  description?: string;
  model_type?: string;
  prompt_template?: string;
  is_active?: boolean;
  category?: string;
  category_id?: number;
  base_color?: string;
}

class AIAgentService {
  /**
   * 利用可能なAIエージェント一覧を取得（認証済みユーザーのみ）
   * @param includeAll - trueの場合、サイドバー非表示のエージェントも含める
   * @returns AIエージェント一覧
   */
  async getActiveAgents(includeAll: boolean = false): Promise<AIAgent[]> {
    try {
      console.log('AIエージェント取得を開始します...');
      
      const config = { 
        timeout: 60000,
        params: includeAll ? { include_all: true } : {}
      };
      // 認証済みユーザー専用のエンドポイントを使用
      const response = await apiRequest.get<{agents?: AIAgent[]}>('/ai-agents/active', config);
      console.log('===== AIエージェントAPI応答 =====');
      console.log('API応答ステータス: 成功');
      
      // レスポンスの形式を確認して適切に処理
      const agents = response.agents || response;
      
      // 受信したエージェントデータを検証
      if (Array.isArray(agents)) {
        console.log('エージェント配列を正常に取得しました:', agents.length);
        const favoriteCount = agents.filter(a => a.is_favorited).length;
        console.log(`お気に入り数: ${favoriteCount}`);
        return agents;
      } else {
        console.error('エージェントデータが配列ではありません:', typeof agents);
        console.error('データ内容:', JSON.stringify(agents).substring(0, 200) + '...');
        // 空の配列を返す前に警告ログを出力
        console.warn('有効なデータが取得できなかったため、空の配列を返します');
        return [];
      }
    } catch (error: any) {
      // より詳細なエラー情報をログに出力
      if (error?.name === 'AxiosError' && error?.code === 'ECONNABORTED') {
        console.error('AIエージェント取得がタイムアウトしました:', error.message);
      } else {
        console.error('AIエージェント取得に失敗しました:', error);
        
        // エラーオブジェクトが存在する場合のみ詳細情報を出力
        if (error) {
          console.error('エラーの詳細:', {
            name: error.name || 'Unknown',
            message: error.message || 'No message',
            code: error.code || 'No code',
            status: error.response?.status,
            data: error.response?.data
          });
        }
      }
      
      // エラーが発生しても空の配列を返して処理を継続できるようにする
      return [];
    }
  }
  
  /**
   * 全AIエージェント一覧を取得（管理者用）
   * @returns AIエージェント一覧
   */
  async getAllAgents(): Promise<AIAgent[]> {
    try {
      const config = { timeout: 15000 }; // タイムアウトを15秒に短縮
      // Backend returns `{ data: { agents: [...] } }` for this admin endpoint.
      // Accept legacy shapes too so callers always receive a plain array.
      const response = await apiRequest.get<any>('/ai-agents', config);
      if (Array.isArray(response)) return response;
      if (response?.data?.agents && Array.isArray(response.data.agents)) return response.data.agents;
      if (response?.agents && Array.isArray(response.agents)) return response.agents;
      console.warn('getAllAgents: unexpected response shape', response);
      return [];
    } catch (error: any) {
      console.error('AIエージェント一覧取得に失敗しました:', error?.message || 'エラー詳細不明');
      throw error;
    }
  }
  
  /**
   * 特定のAIエージェント情報を取得
   * @param agentId AIエージェントID
   * @returns AIエージェント情報
   */
  async getAgentById(agentId: number): Promise<AIAgent> {
    try {
      const config = { timeout: 15000 }; // タイムアウトを15秒に短縮
      return await apiRequest.get<AIAgent>(`/ai-agents/${agentId}`, config);
    } catch (error: any) {
      console.error(`AIエージェント(ID: ${agentId})の取得に失敗しました:`, error?.message || 'エラー詳細不明');
      throw error;
    }
  }
  
  /**
   * 新しいAIエージェントを作成
   * @param data 作成するAIエージェント情報
   * @returns 作成されたAIエージェント
   */
  async createAgent(data: CreateAgentRequest): Promise<AIAgent> {
    try {
      console.log('[createAgent] リクエスト開始:', data);
      const config = { timeout: 15000 };
      const response = await apiRequest.post<{
        success: boolean;
        data: { agent: AIAgent };
        message: string;
      }>('/ai-agents', data, config);
      console.log('[createAgent] レスポンス受信:', response);
      console.log('[createAgent] エージェントデータ:', response.data.agent);
      return response.data.agent;
    } catch (error: any) {
      console.error('[createAgent] エラー発生:', error);
      console.error('[createAgent] エラーレスポンス:', error?.response?.data);
      console.error('[createAgent] エラーステータス:', error?.response?.status);
      throw error;
    }
  }
  
  /**
   * AIエージェント情報を更新
   * @param agentId AIエージェントID
   * @param data 更新するAIエージェント情報
   * @returns 更新後のAIエージェント情報
   */
  async updateAgent(agentId: number, data: UpdateAgentRequest): Promise<AIAgent> {
    try {
      const config = { timeout: 15000 };
      const response = await apiRequest.put<{
        success: boolean;
        data: { agent: AIAgent };
        message: string;
      }>(`/ai-agents/${agentId}`, data, config);
      return response.data.agent;
    } catch (error: any) {
      console.error(`AIエージェント(ID: ${agentId})の更新に失敗しました:`, error?.message || 'エラー詳細不明');
      throw error;
    }
  }
  
  /**
   * AIエージェントの有効/無効を切り替え（管理者用）
   * @param agentId AIエージェントID
   * @param isActive 有効にするかどうか
   * @returns 更新後のAIエージェント情報
   */
  async toggleAgentStatus(agentId: number, isActive: boolean): Promise<AIAgent> {
    try {
      const config = { timeout: 15000 }; // タイムアウトを15秒に短縮
      return await apiRequest.patch<AIAgent>(`/ai-agents/${agentId}/status`, { is_active: isActive }, config);
    } catch (error: any) {
      console.error(`AIエージェント(ID: ${agentId})のステータス変更に失敗しました:`, error?.message || 'エラー詳細不明');
      throw error;
    }
  }
  
  /**
   * AIエージェントを削除（管理者用）
   * @param agentId AIエージェントID
   * @returns 成功したかどうか
   */
  async deleteAgent(agentId: number): Promise<{ success: boolean; message: string }> {
    try {
      const config = { timeout: 15000 }; // タイムアウトを15秒に短縮
      return await apiRequest.delete<{ success: boolean; message: string }>(`/ai-agents/${agentId}`, config);
    } catch (error: any) {
      console.error(`AIエージェント(ID: ${agentId})の削除に失敗しました:`, error?.message || 'エラー詳細不明');
      throw error;
    }
  }

  /**
   * AIチャットモード専用のエージェント一覧を取得
   * @returns AIエージェント一覧（チャットモード専用）
   */
  async getChatModelAgents(): Promise<AIAgent[]> {
    try {
      console.log('AIチャットモード用エージェント取得を開始します...');
      
      const config = { timeout: 60000 };
      const response = await apiRequest.get<{agents?: AIAgent[]}>('/ai-agents/chat-models', config);
      console.log('===== AIチャットモード用エージェントAPI応答 =====');
      console.log('API応答ステータス: 成功');
      
      const agents = response.agents || response;
      
      if (Array.isArray(agents)) {
        console.log('チャットモード用エージェント配列を正常に取得しました:', agents.length);
        return agents;
      } else {
        console.error('エージェントデータが配列ではありません:', typeof agents);
        return [];
      }
    } catch (error: any) {
      console.error('AIチャットモード用エージェント取得に失敗しました:', error?.message || 'エラー詳細不明');
      throw error;
    }
  }
  
  /**
   * AIエージェントの使用統計を取得（管理者用）
   * @param agentId AIエージェントID
   * @returns 使用統計情報
   */
  async getAgentStats(agentId: number): Promise<{
    total_messages: number;
    total_chats: number;
    average_tokens_per_message: number;
  }> {
    try {
      const config = { timeout: 15000 }; // タイムアウトを15秒に短縮
      return await apiRequest.get<{
        total_messages: number;
        total_chats: number;
        average_tokens_per_message: number;
      }>(`/ai-agents/${agentId}/stats`, config);
    } catch (error: any) {
      console.error(`AIエージェント(ID: ${agentId})の統計情報取得に失敗しました:`, error?.message || 'エラー詳細不明');
      throw error;
    }
  }
}

export default new AIAgentService();
